import { IOwnedProduct } from "../../subscriptions/models/OwnedProduct";
import { IProductDescription } from "../../ts/models/product/IProductDescription";
import { ProductSuggestion } from "../autocomplete/models/SearchAutocompleteSuggestions";

export abstract class SearchFilter {
    public type: string;
    public label: string;
    public resultCount: number;

    constructor(type: string, label: string, resultCount: number){
        this.type = type;
        this.label = label;
        this.resultCount = resultCount;
    }

    abstract matchesProduct(productSuggestion: ProductSuggestion): boolean;
    abstract matchesProductDescription(product: IProductDescription): boolean;
    abstract matchesOwnedProduct(product: IOwnedProduct): boolean;

    public static groupSearchFiltersByType(searchFilters: SearchFilter[]): SearchFilter[][]{
        if(!searchFilters || !searchFilters.length) { return []; }

        const results: SearchFilter[][] = [];
        const map: {[key: string]: SearchFilter[]} = {};

        for(const searchFilter of searchFilters){
            if(!map[searchFilter.type]) {
                map[searchFilter.type] = [];
            }

            map[searchFilter.type].push(searchFilter);
        }

        for(const type in map){
            results.push(map[type]);
        }

        return results;
    }
}

export class CategorySearchFilter extends SearchFilter {
    constructor(label: string, resultCount: number = 0){
        super("category", label, resultCount);
    }

    public matchesProduct(productSuggestion: ProductSuggestion): boolean {
        if(!productSuggestion || !productSuggestion.tags || !productSuggestion.tags.length) { return false; }

        return productSuggestion.tags.indexOf(this.label) != -1;
    }

    public matchesProductDescription(productDescription: IProductDescription): boolean {
        if(!productDescription || !productDescription.tags || !productDescription.tags.length) { return false; }

        return productDescription.tags.indexOf(this.label) != -1;
    }

    public matchesOwnedProduct(ownedProduct: IOwnedProduct): boolean {
        console.log("Not yet implemented");
        return true;
    }
}

export class PriceSearchFilter extends SearchFilter {
    private constructor(label: string, min: number, max: number, lowerBound: number = min, upperBound: number = max){
        super("price", label, 0);

        this.min = min;
        this.max = max || 100;
        
        this.lowerBound = lowerBound;
        this.upperBound = upperBound;
    }

    public lowerBound: number;
    public upperBound: number;

    public min: number;
    public max: number;

    private isWithinBounds(price: number){
        if(price === undefined) { return true; }
        
        return price >= (this.lowerBound || 0) && (price <= ((this.upperBound || 10000)));
    }

    public matchesProduct(productSuggestion: ProductSuggestion): boolean {
        if(!productSuggestion) { return false; }

        return this.isWithinBounds(productSuggestion.getPrice());
    }

    public matchesProductDescription(productDescription: IProductDescription): boolean {
        console.log("Not yet implemented");
        return false;
    }

    public matchesOwnedProduct(ownedProduct: IOwnedProduct): boolean {
        console.log("Not yet implemented");
        return true;
    }

    static createCopy(priceSearchFilter: PriceSearchFilter, lowerBound: number, upperBound: number): PriceSearchFilter {
		return new PriceSearchFilter(priceSearchFilter.label, priceSearchFilter.min, priceSearchFilter.max, lowerBound, upperBound);
	}

    public static createPriceSearchFilter(productSuggestions: ProductSuggestion[], oldPriceSearchFilter: PriceSearchFilter): PriceSearchFilter {
        if(productSuggestions == null || productSuggestions.length == 0){
            return oldPriceSearchFilter == null ? new PriceSearchFilter("$0 - $400", 0, 400) : oldPriceSearchFilter;
        }
        
        let maxPrice = productSuggestions[0].getPriceWithoutDiscount() || productSuggestions[0].getPrice();

        for(let i = 1; i < productSuggestions.length; i++){
            const productSuggestion = productSuggestions[i];
            
            const price = productSuggestion.getPriceWithoutDiscount() || productSuggestion.getPrice();
            if(price > maxPrice){
                maxPrice = price;
            }
        }

        if(maxPrice < 1 || !maxPrice){
            maxPrice = 400;
        }

        return new PriceSearchFilter(
            `$0 - $${maxPrice}`, 
            0, 
            maxPrice, 
            Math.min(oldPriceSearchFilter != null ? oldPriceSearchFilter.min : 0, 0),
            Math.max(oldPriceSearchFilter != null ? oldPriceSearchFilter.max : maxPrice, maxPrice)
        );
    }
}

export class ProductTypeSearchFilter extends SearchFilter {
    constructor(label: string, resultCount: number = 0){
        super("product", label, resultCount);
    }

    public static createBookProductTypeSearchFilter(){
        return new ProductTypeSearchFilter("book");
    }

    public static createVideoProductTypeSearchFilter(){
        return new ProductTypeSearchFilter("liveVideo");
    }

    public static createBundleTypeSearchFilter(){
        return new ProductTypeSearchFilter("bundle");
    }

    public static createLiveProjectTypeSearchFilter(){
        return new ProductTypeSearchFilter("liveProject");
    }

    public static createEarlierEditionTypeSearchFilter(){
        return new ProductTypeSearchFilter("Earlier editions");
    }

    public matchesOwnedProduct(ownedProduct: IOwnedProduct): boolean {
        if(ownedProduct == null) { return false; }

        return ownedProduct.productType == this.label;
    }

    public matchesProduct(productSuggestion: ProductSuggestion): boolean {
        if(!productSuggestion) { return false; }

        if(this.label == "book"){
            return !productSuggestion.isLivevideo && !productSuggestion.isBundle && !productSuggestion.isEarlierEdition;
        }
        else if(this.label == "liveVideo"){
            return productSuggestion.isLivevideo;
        }
        else if(this.label == "bundle"){
            return productSuggestion.isBundle;
        }
        else if(this.label == "liveProject"){
            return productSuggestion.isLiveproject;
        }
        else if(this.label == "Earlier editions"){
            return productSuggestion.isEarlierEdition;
        }

        return false;
    }

    public matchesProductDescription(productSuggestion: IProductDescription): boolean {
        if(!productSuggestion) { return false; }

        return this.label == productSuggestion.productType;
    }
}

export class RatingSearchFilter extends SearchFilter{
    private constructor(label: string, minimumRating: number, resultCount: number = 0){
        super("customer rating", label, resultCount);

        this.minimumRating = minimumRating;
    }

    public minimumRating: number;

    public matchesProduct(productSuggestion: ProductSuggestion): boolean {
        if(!productSuggestion 
        || !productSuggestion.productInfo
        || !productSuggestion.productInfo.additionalProductInfo
        || !productSuggestion.productInfo.additionalProductInfo.rating) { return true; }

        let rating = Math.max(productSuggestion.productInfo.additionalProductInfo.rating, 0);

        return rating >= this.minimumRating;
    }

    public matchesProductDescription(productDescription: IProductDescription): boolean {
        console.log("Not yet implemented");
        return false;
    }

    public matchesOwnedProduct(ownedProduct: IOwnedProduct): boolean {
        console.log("Not yet implemented");
        return false;
    }

    static create(rating: number = 0): RatingSearchFilter {
		return new RatingSearchFilter("customer rating", rating);
	}
}